import type { Editor } from '@tiptap/core';
import { DOMSerializer, type Node as ProseMirrorNode } from '@tiptap/pm/model';

import { renderContent } from '../doc-engine';
import type { TDocDiffOptions, TDocDiffRuntime } from '../doc-engine';
import type { TProseMirrorRange } from './scrollContent/types';

export type TResolveContentOptions = {
  runtime: TDocDiffRuntime;
  opts: TDocDiffOptions;
};

type TSelectionSegment = { owner: ProseMirrorNode | null } & ({ text: string } | { atom: ProseMirrorNode });

export const normalizeProseMirrorRange = (editor: Editor, range: TProseMirrorRange): TProseMirrorRange | null => {
  const docEnd = editor.state.doc.content.size;
  const from = Math.max(0, Math.min(range.from, range.to, docEnd));
  const to = Math.max(0, Math.min(Math.max(range.from, range.to), docEnd));
  return from < to ? { from, to } : null;
};

const collapseSpaces = (value: string): string => value.replace(/\s+/gu, ' ').trim();

/** Splitting element-only containers keeps neighbouring table cells from running into one word. */
export const nodeText = (node: globalThis.Node): string => {
  if (!(node instanceof Element)) return collapseSpaces(node.textContent ?? '');

  const children = [...node.childNodes];
  const holdsOwnText = children.some(
    (child) => child.nodeType === globalThis.Node.TEXT_NODE && (child.textContent ?? '').trim()
  );
  if (holdsOwnText) return collapseSpaces(node.textContent ?? '');

  return children.map(nodeText).filter(Boolean).join('\n');
};

// The doc-engine awaits a block still being fetched; its node view would give the placeholder.
const resolveAtomText = async (
  editor: Editor,
  node: ProseMirrorNode,
  { runtime, opts }: TResolveContentOptions
): Promise<string> => {
  const container = document.createElement('div');
  container.appendChild(DOMSerializer.fromSchema(editor.schema).serializeNode(node));

  // No content hooks: the LaTeX one draws a formula three times over, so its text arrives tripled.
  const rendered = await renderContent(container.innerHTML, runtime, opts, 'current');
  const target = document.createElement('div');
  target.innerHTML = rendered;
  return nodeText(target);
};

export const getResolvedProseMirrorText = async (
  editor: Editor,
  range: TProseMirrorRange,
  options: TResolveContentOptions
): Promise<string> => {
  const normalizedRange = normalizeProseMirrorRange(editor, range);
  if (!normalizedRange) return '';

  const segments: TSelectionSegment[] = [];
  editor.state.doc.nodesBetween(normalizedRange.from, normalizedRange.to, (node, pos, parent) => {
    const owner = node.isBlock ? node : (parent ?? null);

    if (node.isText && node.text) {
      const from = Math.max(normalizedRange.from, pos) - pos;
      const to = Math.min(normalizedRange.to, pos + node.nodeSize) - pos;
      segments.push({ owner, text: node.text.slice(from, to) });
      return false;
    }

    if (node.type.name === 'hardBreak') {
      segments.push({ owner, text: '\n' });
      return false;
    }

    if (node.isAtom) {
      segments.push({ owner, atom: node });
      return false;
    }

    return true;
  });

  const resolved = await Promise.all(
    segments.map(async (segment) => ('atom' in segment ? resolveAtomText(editor, segment.atom, options) : segment.text))
  );

  let result = '';
  let previousTextOwner: ProseMirrorNode | null = null;
  resolved.forEach((value, index) => {
    if (!value) return;
    const { owner } = segments[index];
    if (previousTextOwner && previousTextOwner !== owner && !result.endsWith('\n') && !value.startsWith('\n')) {
      result += '\n';
    }
    result += value;
    previousTextOwner = owner;
  });

  return result;
};
